import React, { useContext, useEffect } from 'react';
import UserDetailsContext from '../context/userDetails.context';
import LocationsContext from '../context/locations.Context';
import ThemeContext from '../context/theme.Context';
import LoadingContext from '../context/loading.context';
import NotificationContext from '../context/notification.Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinkContainer } from 'react-router-bootstrap';
import Device from './Device';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import * as Constants from '../modules/constants';


function Devices() {

    const { locations, activeLocationId, activeGroupIds, setActiveGroupId, setDeviceState } = useContext(LocationsContext);
    const { showLoading, hideLoading } = useContext(LoadingContext);
    const { token, setUserDetails } = useContext(UserDetailsContext);
    const { showMessage, setServerUnreachable } = useContext(NotificationContext);

    /* 
     * Retrieve device state updates from the server every x seconds, for all visible devices (devices in the active group for the active location).
     */
    const UPDATE_INTERVAL = 15;  // in seconds
    const apiHostName = Constants.API_URL;

    useEffect(() => {
        const deviceUpdateTimer = setInterval(updateDeviceStates, UPDATE_INTERVAL * 1000);
        return () => {
            clearInterval(deviceUpdateTimer);
        };

        function getActiveGroupIdHook() {

            var groupId;

            if (activeGroupIds && activeGroupIds[getActiveLocationIdHook().toString()])
                groupId = activeGroupIds[getActiveLocationIdHook().toString()].groupId;

            return groupId || 'all';
        }

        function getActiveLocationIdHook() {
            if (activeLocationId)
                return activeLocationId;
            if (locations)
                return Object.values(locations)[0].id.toString();
            return '0';
        }

        function updateDeviceStates() {

            var url;

            showLoading();

            if (getActiveGroupIdHook() === 'all') {
                // Get location devices
                url = apiHostName + '/v1/locations/' + getActiveLocationIdHook();
            }
            else {
                // Get group devices
                url = apiHostName + '/v1/groups/' + getActiveGroupIdHook();
            }
            fetch(url, {
                mode: "cors",
                method: 'GET',
                headers: { "Authorization": "Bearer " + token },
                cache: 'no-cache'
            })
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error("Invalid network response");
                    }
                    setServerUnreachable(false);
                    return response.json();
                })
                .then((result) => {
                    // Result contains (a.o.) list of devices - update them now:
                    Object.values(result.devices).forEach(device => {
                        setDeviceState(getActiveLocationIdHook(), device);
                    });

                    hideLoading();
                })
                .catch(function (error) {
                    // TODO: set visual cue and error text to be displayed on login screen? (No: app should also work in offline mode, so actions should display visual cues, but status updates shouldn't)
                    setServerUnreachable(true);
                    console.error("Update failed...");
                    console.error(error);
                    hideLoading();
                });
        }

    }, [locations, activeLocationId, apiHostName, activeGroupIds, hideLoading, showLoading, token, setDeviceState]);


    function selectGroup(eventKey, event) {
        setActiveGroupId(activeLocationId, eventKey);
    }

    function getActiveGroupId() {

        var groupId;

        if (activeGroupIds && activeGroupIds[getActiveLocationId().toString()])
            groupId = activeGroupIds[getActiveLocationId().toString()].groupId;

        return groupId || 'all';
    }

    function getActiveLocationId() {
        if (activeLocationId)
            return activeLocationId;
        if (locations)
            return Object.values(locations)[0].id.toString();
        return '0';
    }


    function activeGroupDevices() {
        var deviceIDs;
        if (getActiveGroupId() === 'all')
            if (locations && locations[getActiveLocationId().toString()] && locations[getActiveLocationId().toString()].devices)
                deviceIDs = Object.keys(locations[getActiveLocationId().toString()].devices).map((key) => { return [key]; });
            else
                deviceIDs = [];
        else
            if (locations && locations[getActiveLocationId().toString()] && locations[getActiveLocationId().toString()].groups[getActiveGroupId()])
                deviceIDs = locations[getActiveLocationId().toString()].groups[getActiveGroupId()].deviceIDs;
            else
                deviceIDs = [];

        var devices = [];
        if (!deviceIDs)
            deviceIDs = [];
        deviceIDs.forEach(deviceId => {
            devices.push(locations[getActiveLocationId().toString()].devices[deviceId]);
        });
        return devices;
    }


    function checkStatus(response) {
        if (response.status >= 200 && response.status < 300) {
            return response;
        } else {
            var error = new Error(response.statusText);
            error.response = response;
            if (error.response.status === 401) {
                // Not authorized --> empty user details, so we get redirected to login page
                setUserDetails({ userName: '', email: '', userLocation: { name: '', id: 0 }, token: '' });
            }
            throw error;
        }
    }


    function setCurrentState(device, newState) {
        // Update state property
        const updatedDevice = { ...device };
        updatedDevice.lastState = newState;
        setDeviceState(getActiveLocationId(), updatedDevice);
    }
    function setCurrentLevel(device, newLevel) {
        // Update level property
        const updatedDevice = { ...device };
        updatedDevice.lastLevel = newLevel;
        setDeviceState(getActiveLocationId(), updatedDevice);
    }



    function sendCommandToGroup(command) {
        activeGroupDevices().forEach(device => {

            var cmd;
            var targetState;
            var targetLevel;

            cmd = null;
            targetState = null;
            targetLevel = 0;

            showLoading();
            if (command === 'power') {
                // Turn device on or off, depending on its current state:
                if (device.lastState === 'Off' || device.lastState === 'Off/Off' || device.lastState === 'On/Off' || device.lastState === 'Idle') {
                    // Switch the device on
                    cmd = 'on';
                    targetState = 'On';
                    if (device.deviceType.startsWith('SF-')) {
                        // Bio-ethanol burner
                        targetState = 'Starting';
                    }
                    else if (device.deviceType.startsWith('SW-')) {
                        // Switch
                        cmd = 'switchRelais:1=on&2=on';
                        targetState = 'On/On';
                    }
                }
                else if (device.lastState === 'On' || device.lastState === 'On/On' || device.lastState === 'Warming up' || device.lastState === 'Starting') {
                    // Switch the device off
                    cmd = 'off';
                    targetState = 'Off';
                    if (device.deviceType.startsWith('SF-')) {
                        // Bio-ethanol burner
                        targetState = 'Turning off';
                    }
                    else if (device.deviceType.startsWith('SW-')) {
                        // Switch
                        cmd = 'switchRelais:1=on&2=off';
                        targetState = 'On/Off';
                    }
                }
                else {
                    // Other state --> do nothing (may be e.g. 'Turning off', in case no power command is possible...)
                }
            }
            else if (command === 'up') {
                // Send 'up' command:
                if (device.lastState === 'On' || device.lastState === 'Warming up' || device.lastState === 'Starting') {
                    if ((device.deviceType.startsWith('SF-') && device.lastLevel < 5) || (device.deviceType.startsWith('IH-') && device.lastLevel < 4)) {
                        // Bio-ethanol burner - maxLevel = 5; Infrared heater - maxLevel = 4
                        cmd = 'up';
                        targetLevel = device.lastLevel + 1;
                    }
                }
            }
            else if (command === 'down') {
                // Send 'up' command:
                if (device.lastState === 'On' || device.lastState === 'Warming up' || device.lastState === 'Starting') {
                    if ((device.deviceType.startsWith('SF-') || device.deviceType.startsWith('IH-')) && device.lastLevel > 1) {
                        // Bio-ethanol burner - maxLevel = 5; Infrared heater - maxLevel = 4
                        cmd = 'down';
                        targetLevel = device.lastLevel - 1;
                    }
                }
            }

            if (!cmd) {
                hideLoading();
            }
            else {
                // Send command to device:
                fetch(apiHostName + '/v1/devices/' + device.id + '/' + cmd,
                    {
                        method: 'PUT',
                        mode: "cors",
                        headers: { "Authorization": "Bearer " + token }
                    })
                    .then(checkStatus)
                    .then(() => {
                        if (targetState)
                            setCurrentState(device, targetState);
                        if (targetLevel > 0)
                            setCurrentLevel(device, targetLevel);
                        hideLoading();
                    })
                    .catch(function (error) {
                        // TODO: set visual cue and error text to be displayed on login screen
                        showMessage({ newMessage: 'Network error.' });
                        hideLoading();
                    });
            }
        });
    }

    function clickUp() {
        sendCommandToGroup('up');
    }

    function clickDown() {
        sendCommandToGroup('down');
    }

    function clickPowerButton() {
        sendCommandToGroup('power');
    }


    // Get a list of groups & devices from the API:
    return (
        <ThemeContext.Consumer>
            {(themeContext) => (
                <React.Fragment>
                    <Navbar bg={themeContext.theme} variant={themeContext.theme} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                        <Nav className="mr-auto" activeKey={getActiveGroupId()}>
                            <Nav.Item key="all">
                                <Nav.Link onSelect={selectGroup} eventKey={"all"}>All</Nav.Link>
                            </Nav.Item>
                            {locations && locations[getActiveLocationId().toString()] && locations[getActiveLocationId().toString()].groups && Object.values(locations[getActiveLocationId().toString()].groups).map(group => (
                                <Nav.Item key={group.id}>
                                    <Nav.Link onSelect={selectGroup} eventKey={group.id} >{group.name}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Navbar>
                    {// Add a group command bar
                    }
                    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingLeft: '6px', paddingRight: '6px', border: '1px solid rgba(0, 0, 0, .125)' }}>
                        <div style={{ display: 'block', paddingTop: '2px' }}>
                            <LinkContainer to={'/groups'}>
                                <Button variant={themeContext.theme} title="Manage groups"><FontAwesomeIcon icon="layer-group" /></Button>
                            </LinkContainer>
                            <span className={'h6 bg-' + themeContext.theme + ' text-' + themeContext.textColor} >Group commands</span>
                            <ButtonGroup className="float-right">
                                <Button variant={themeContext.theme} onClick={() => { clickUp(); }} ><FontAwesomeIcon icon="angle-up" /></Button>
                                <Button variant={themeContext.theme} onClick={() => { clickDown(); }}><FontAwesomeIcon icon="angle-down" /></Button>
                                <Button variant={themeContext.theme} onClick={() => { clickPowerButton(); }}><FontAwesomeIcon icon="power-off" /></Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    {
                        activeGroupDevices().length > 0 && activeGroupDevices().map(deviceObj =>
                            <Device name={deviceObj.description} key={deviceObj.id} device={deviceObj} locationId={getActiveLocationId()} detailsVisible={false} />
                        )
                    }
                </React.Fragment>
            )
            }
        </ThemeContext.Consumer >
    );
}

export default Devices;


// WEBPACK FOOTER //
// ./src/components/Devices.js